// Type Race - one short passage a day (the same for everyone, picked by the New York date),
// typed against the clock. Speed and accuracy are worked out here in the browser; the only
// thing kept is this device's best run for today.
import { el, btn, note, wrap, copy, announce } from "./ui.js";
import { readJSON, writeJSON } from "../shared/store.js";
import { dayKey, dayNumber, passageFor, score, shareLine, countEdit } from "./typerace-core.js";

const KEY = "revault:typerace";

function readBest(day) {
	const v = readJSON(KEY, null);
	return v && v.day === day && Number.isFinite(v.wpm) && Number.isFinite(v.accuracy) ? v : null;
}

const touchOnly = () => {
	try { return matchMedia("(pointer: coarse)").matches && !matchMedia("(any-pointer: fine)").matches; } catch { return false; }
};

export function typerace(root) {
	const s = wrap(root); s.classList.add("rv-tr");
	const day = dayKey();
	const n = dayNumber(day);
	const passage = passageFor(day);
	const target = passage.text;

	const head = el("div", "rv-tr-head");
	head.append(el("h2", "rv-tool-title", n >= 1 ? "Type Race #" + n : "Type Race preview"), el("span", "rv-tnote", "A new passage every day"));
	s.appendChild(head);
	if (touchOnly()) { const n2 = note("Type Race is made for a keyboard. You can still try it here; your speed may be lower on a phone."); n2.classList.add("rv-tr-phone"); s.appendChild(n2); }

	const box = el("div", "rv-tr-box");
	const plain = el("p", "rv-sr-only", target);
	const shown = el("p", "rv-tr-text"); shown.setAttribute("aria-hidden", "true");
	const spans = Array.from(target, (ch) => { const sp = el("span", null, ch); shown.appendChild(sp); return sp; });
	const cite = el("p", "rv-tr-cite", passage.author + ", " + passage.source);
	box.append(plain, shown, cite);

	const area = el("textarea", "rv-tarea rv-tr-input");
	area.id = "rv-tr-input";
	area.rows = 3;
	area.maxLength = target.length;
	area.setAttribute("autocomplete", "off");
	area.setAttribute("autocorrect", "off");
	area.setAttribute("autocapitalize", "off");
	area.spellcheck = false;
	area.setAttribute("aria-describedby", "rv-tr-hint");
	const label = el("label", "rv-field-label", "Type the passage above");
	label.htmlFor = area.id;
	const hint = el("p", "rv-tnote", "The clock starts with your first key. Pasting is turned off.");
	hint.id = "rv-tr-hint";

	const stats = el("div", "rv-tr-stats"); stats.setAttribute("aria-hidden", "true");
	const stat = (name) => { const w = el("div", "rv-tr-stat"); const v = el("span", "rv-tr-stat-val", "0"); w.append(v, el("span", "rv-tr-stat-name", name)); stats.appendChild(w); return v; };
	const timeVal = stat("seconds"), wpmVal = stat("wpm"), accVal = stat("accuracy");
	timeVal.textContent = "0.0"; accVal.textContent = "100%";

	const result = el("div", "rv-tr-result");
	result.hidden = true;
	const bestLine = el("p", "rv-tnote rv-tr-best");

	s.append(box, label, area, hint, stats, result, bestLine);

	let prev = "", startedAt = 0, keystrokes = 0, errors = 0, ticker = 0, done = false;

	function showBest() {
		const b = readBest(day);
		bestLine.textContent = b ? "Your best today on this device: " + b.wpm + " wpm, " + b.accuracy + "% accuracy." : "";
	}
	function caret() {
		const a = area.selectionStart, b = area.selectionEnd;
		return Number.isInteger(a) && a === b ? a : -1;
	}
	function paint() {
		const v = area.value;
		const at = done ? -1 : caret();
		for (let i = 0; i < spans.length; i++) {
			const c = i < v.length ? (v[i] === target[i] ? "ok" : "bad") + (i === at ? " cur" : "") : i === at ? "cur" : "";
			if (spans[i].className !== c) spans[i].className = c;
		}
	}
	function now() { return performance.now(); }
	function current() { return score({ target, typed: area.value, ms: startedAt ? now() - startedAt : 0, keystrokes, errors }); }
	function tick() {
		if (!area.isConnected) { clearInterval(ticker); return; }
		const r = current();
		timeVal.textContent = r.seconds.toFixed(1);
		wpmVal.textContent = String(r.wpm);
		accVal.textContent = (keystrokes ? r.accuracy : 100) + "%";
	}
	// A tab left open past midnight in New York picks up the new day's passage before the next race.
	function newDay() {
		if (dayKey() === day || !root.isConnected) return false;
		clearInterval(ticker);
		typerace(root);
		const fresh = root.querySelector("#rv-tr-input");
		if (fresh) fresh.focus();
		announce("A new passage is ready for today.");
		return true;
	}
	function reset() {
		if (newDay()) return;
		clearInterval(ticker);
		prev = ""; startedAt = 0; keystrokes = 0; errors = 0; done = false;
		area.value = ""; area.readOnly = false;
		result.hidden = true; result.textContent = "";
		timeVal.textContent = "0.0"; wpmVal.textContent = "0"; accVal.textContent = "100%";
		paint();
		area.focus();
	}
	function finish() {
		done = true;
		clearInterval(ticker);
		const r = score({ target, typed: area.value, ms: now() - startedAt, keystrokes, errors });
		tick();
		area.readOnly = true;
		paint();
		const best = readBest(day);
		if (!best || r.wpm > best.wpm || (r.wpm === best.wpm && r.accuracy > best.accuracy)) writeJSON(KEY, { day, wpm: r.wpm, accuracy: r.accuracy });
		showBest();

		const line = shareLine(n, r.wpm, r.accuracy);
		result.textContent = "";
		const title = el("h3", "rv-tr-result-title", "Finished");
		title.tabIndex = -1;
		const big = el("div", "rv-tr-result-big");
		big.append(el("span", "rv-grade-big", String(r.wpm)), el("span", "rv-tr-result-unit", "wpm"));
		const sub = el("p", "rv-tr-result-sub", r.accuracy + "% accuracy in " + r.seconds.toFixed(1) + " seconds");
		const share = el("output", "rv-tr-share", line);
		share.setAttribute("aria-label", "Share line");
		const copyBtn = btn("Copy result", () => { copy(line); copyBtn.textContent = "Copied"; announce("Result copied"); setTimeout(() => { copyBtn.textContent = "Copy result"; }, 1500); }, "rv-tbtn rv-tbtn-accent");
		const again = btn("Race again", reset);
		const actions = el("div", "rv-trow"); actions.append(copyBtn, again);
		result.append(title, big, sub, share, actions);
		result.hidden = false;
		announce("Finished. " + r.wpm + " words per minute, " + r.accuracy + " percent accuracy.");
		copyBtn.focus();
	}

	// No pasting, dropping or new lines: the passage has to be typed.
	area.addEventListener("beforeinput", (e) => {
		if (done) { e.preventDefault(); return; }
		if (/^insertFrom(Paste|Drop|Yank)|^insert(LineBreak|Paragraph)$/.test(e.inputType || "")) e.preventDefault();
	});
	area.addEventListener("paste", (e) => e.preventDefault());
	area.addEventListener("drop", (e) => e.preventDefault());
	area.addEventListener("keydown", (e) => { if (e.key === "Enter") e.preventDefault(); });
	area.addEventListener("input", () => {
		if (done) { area.value = prev; return; }
		const v = area.value.replace(/[\r\n]/g, "");
		if (v !== area.value) area.value = v;
		if (!startedAt && v.length) {
			if (newDay()) return;
			startedAt = now(); ticker = setInterval(tick, 200);
		}
		const d = countEdit(prev, v, target, area.selectionEnd);
		keystrokes += d.keystrokes; errors += d.errors;
		prev = v;
		paint();
		if (v.length >= target.length) finish();
		else tick();
	});

	// Keep the cursor marker under the caret when it moves without typing.
	for (const ev of ["keyup", "click", "focus", "blur", "select"]) area.addEventListener(ev, () => { if (!done) paint(); });

	paint();
	showBest();
}
